import httpClient from "@/httpClient"
import vaiClient from "@/vaiClient"
import { createCancellable } from "@/utils/cancellable"
import { CANCEL_SOURCES } from "@/constants/CANCEL_SOURCES"
import { COMPANY_SLUG_PLACEHOLDER } from '@/constants/COMPANY_SLUG'

let getRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
let getKanbanRecordsCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD)
let getCardRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
let getRecordsCountCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD_COUNT)

const getRecords = (filters, dropPrevious = false, config = null) => {
  if (dropPrevious) {
    getRecordsCancellable.abort()
    getRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/all',
    filters,
    config || getRecordsCancellable.config,
  )
}

// Kanban board columns. All three entry points share getKanbanRecordsCancellable,
// which wraps a single AbortController, so calling with dropPrevious: true (e.g. a
// full board reload) aborts any in-flight per-column "load more" — intentional, since
// a filter change should supersede a load-more.
const getKanbanRecords = (payload, dropPrevious = false) => {
  if (dropPrevious) {
    getKanbanRecordsCancellable.abort()
    getKanbanRecordsCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/kanban',
    payload,
    getKanbanRecordsCancellable.config,
  )
}

const getCardRecords = (payload, dropPrevious = false) => {
  if (dropPrevious) {
    getCardRecordsCancellable.abort()
    getCardRecordsCancellable = createCancellable(CANCEL_SOURCES.RECORDS_FILTER)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/cards',
    payload,
    getCardRecordsCancellable.config,
  )
}

// The kanban board's own total for the active filters and status pills - the
// number /quotes/all would paginate for the same user, independent of which
// columns are loaded. Called only when the filters or status pills actually
// change (see requestBoardCount in useKanbanBoard.ts), so every real call
// supersedes whatever count is still in flight for the previous filters.
const getRecordsCount = (payload, dropPrevious = false) => {
  if (dropPrevious) {
    getRecordsCountCancellable.abort()
    getRecordsCountCancellable = createCancellable(CANCEL_SOURCES.KANBAN_BOARD_COUNT)
  }

  return httpClient.post(
    '{currentCompanySlug}/quotes/count',
    payload,
    getRecordsCountCancellable.config,
  )
}

const exportRecords = (filters) => httpClient.post(
  '{currentCompanySlug}/quotes/export',
  filters,
)

const createRecord = (company, payload, tryCount = 0) => {
  return httpClient({
    method: 'POST',
    url: `${company || '{currentCompanySlug}'}/quotes`,
    data: payload,
    headers: {
      notification: 'silent',
    },
    hideValidationSections: true,
  }).catch((e) => {
    if (e?.code === "ERR_NETWORK" && tryCount < 4) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          createRecord(company, payload, tryCount + 1)
            .then(resolve)
            .catch(reject)
        }, 5000)
      })
    }

    throw e
  })
}

export const recordVAIParse = (payload, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'POST',
    url: `${import.meta.env.VITE_AI_EXTRACT_BASE_URL}/${company || '{currentCompanySlug}'}/vsimple-ai/extract-document-fields`,
    data: payload,
  })
}

export const recordVAIParseExistingRecord = (payload, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'POST',
    url: `${import.meta.env.VITE_AI_EXTRACT_BASE_URL}/${company || '{currentCompanySlug}'}/vsimple-ai/extract-file-fields`,
    data: payload,
  })
}

const editRecord = ({ id, payload }, company = '{currentCompanySlug}', ignoreEventBus = false) => httpClient.patch(`${company}/quotes/${id}`, payload, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

const deleteRecord = ({ id }, company = '{currentCompanySlug}') => httpClient.delete(`${company}/quotes/${id}`)

const getRecord = (id, params, company = '{currentCompanySlug}', config = {}) => httpClient.get(`${company}/quotes/${id}`, { params, ...config })

export const readRecordStatuses = (company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/statuses`)

const getRecordActivityStream = (id, params, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/activity-stream`, {
  params,
  ignoreGlobalNotFoundCatch: true,
})

export const getRecordActivityStreamGroup = (recordId, postGroupId, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${recordId}/activity-stream/${postGroupId}`, {
  ignoreGlobalNotFoundCatch: true,
})

const getRecordFiles = ({ id, group }, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/file-attachment`, {
  params: { group },
  ignoreGlobalNotFoundCatch: true,
})

// Moves files of one file type into a subfolder (subfolder null = directly in
// the type). A 403 is FilePolicy on one of the files, not a missing page, so the
// global 403 screen and toast are skipped and the caller reports the error. A
// 404 (a file deleted meanwhile) is also the caller's: it must not replace the
// record page with the Not found screen.
export const moveRecordFilesToSubfolder = ({ recordId, fileIds, subfolder }, company = COMPANY_SLUG_PLACEHOLDER) => httpClient.patch(
  `${company}/quotes/${recordId}/file-attachment/subfolder`,
  { fileIds, subfolder },
  { skipDefaultForbiddenBehaviour: true, ignoreGlobalNotify: true, ignoreGlobalNotFoundCatch: true },
)

// Renames a subfolder on every file of one record + file type (D12). Renaming
// onto an existing name merges the two; the backend keeps that one's casing.
// Responds with the renamed files ({ data: [...] }). The Files tab shows 403,
// 404 and 422 itself (inline error or toast), so none of them reaches the
// global forbidden or Not found screens.
export const renameRecordFileSubfolder = (
  { recordId, fileGroupId, from, to },
  company = COMPANY_SLUG_PLACEHOLDER,
) => httpClient.patch(
  `${company}/quotes/${recordId}/file-subfolders`,
  { fileGroupId, from, to },
  { skipDefaultForbiddenBehaviour: true, ignoreGlobalNotify: true, ignoreGlobalNotFoundCatch: true },
)

const getRecordFileGroups = (id, company = '{currentCompanySlug}') => httpClient.get(`${company}/quotes/${id}/files-groups`, {
  ignoreGlobalNotFoundCatch: true,
})

const createMessage = ({ id, payload }, email, tryCount = 0, company = '{currentCompanySlug}') => {
  return httpClient({
    method: 'PUT',
    url: `${company}/quotes/${id}/activity-stream/messages`,
    data: payload,
    skipResponseInterceptor: tryCount < 4,
  }).catch((e) => {
    if (e?.code === "ERR_NETWORK" && tryCount < 4) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          createMessage({ id, payload }, email, tryCount + 1, company)
            .then(resolve)
            .catch(reject)
        }, 5000)
      })
    }

    throw e
  })
}

const updateMessage = ({ id, uuid, data }, company = '{currentCompanySlug}') => httpClient.put(
    `${company}/quotes/${id}/activity-stream/messages/${uuid}`,
  {
    ...data,
  },
)

const updateRecordStatus = ({ id, status, fields }, ignoreEventBus = false, company = '{currentCompanySlug}') => httpClient.patch(`${company}/quotes/${id}`, { status, fields }, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

const updateRecordType = ({ id, recordType, fields }, ignoreEventBus = false) => httpClient.patch(`{currentCompanySlug}/quotes/${id}`, { recordType, fields }, {
  validationRecordId: id,
  ignoreEventBus,
  headers: ignoreEventBus ? { notification: 'silent' } : undefined,
})

export const getFiltersStructure = (forceCompany = null) =>
  httpClient.get(`${forceCompany || '{currentCompanySlug}'}/quotes/filters`)

const addCollaborators = ({ id, accountIds = [] }, company = '{currentCompanySlug}') => httpClient({
  method: 'POST',
  url: `${company}/records/${id}/collaborators/add`,
  data: { accountIds },
  headers: { 'Content-Type': 'application/json', notification: 'silent' },
})

const removeCollaborators = ({ id, accountIds }, company = '{currentCompanySlug}') =>
  httpClient.post(`${company}/records/${id}/collaborators/remove`,{ accountIds } )

const moveChildRecord = ({ fromRecordId, objectId, toRecordId }, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${fromRecordId}/customFields/objects/${objectId}/move`,
  { recordId: toRecordId },
)

const moveChildRecordMultiple = ({ fromRecordId, objectIds, toRecordId }, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${fromRecordId}/customFields/objects/mass-move`,
  {
    recordId: toRecordId,
    objectIds,
  },
)
const deleteObjectChild = ({ objectId, data }, company = '{currentCompanySlug}') => httpClient.delete(
  `${company}/quotes/customFields/${objectId}/objects`,
  {
    data,
  },
)

const editObjectChild = ({ objectId, fieldId, data }) => httpClient.put(
  `{currentCompanySlug}/quotes/customFields/${objectId}/objects/${fieldId}`,
  {
    data,
  },
)

const createObjectChildren = ({ customFieldSlug, recordId, data }) => httpClient.post(
  `{currentCompanySlug}/quotes/${recordId}/customFields/${customFieldSlug}/mass-create`,
  {
    data,
  },
)

// `config` is forwarded to axios untouched so a caller can opt out of the
// global error handling. It defaults to nothing, so the record's own tables
// keep the interceptor behaviour they have always had; the Gantt, which fans
// out to every section at once, opts out (see useRecordGanttSources.ts).
export const getObjectChildren = ({ objectId }, company = '{currentCompanySlug}', config = {}) => httpClient.get(
  `${company}/quotes/customFields/${objectId}/objects`,
  config,
)

const getObjectChildrenMeta = ({ objectId }, company = '{currentCompanySlug}') => httpClient.get(
  `${company}/quotes/customFields/${objectId}/objects/meta`,
)

// `config` is merged into the axios options, defaulting to nothing so the
// record's own tables keep the global 422 toast they have always had. The
// Gantt opts out of it: apiErrorHandler reports the bare `Field is Required`,
// while the Gantt says which column it was.
const patchObjectField = ({ data, recordId }, company = '{currentCompanySlug}', config = {}) => httpClient.put(
  `${company}/quotes/customFields/objects`,
  {
    ...data,
  },
  {
    validationRecordId: recordId || null,
    ...config,
  },
)

const patchObjectFieldDryRun = ({ data }, company = '{currentCompanySlug}') => httpClient.put(
  `${company}/quotes/customFields/objects?dry-run`,
  {
    data,
  },
  {
    ignoreGlobalNotify: true,
  },
)

const sortObjectFiedlds = ({ recordId, objectSlug, data }, company = '{currentCompanySlug}') => httpClient.put(
  `${company}/quotes/${recordId}/customFields/${objectSlug}/objects/sort`,
  {
    ...data,
  },
)

const templateDocumentsList = (company = '{currentCompanySlug}') => httpClient.get(
  `${company}/supplierCompany/template-documents`,
)

const getPersistentColumns = () => httpClient.get(
  '{currentCompanySlug}/quotes/persistent-columns',
)

const savePersistentColumns = (columns) => httpClient.post(
  '{currentCompanySlug}/quotes/persistent-columns',
  {
    columns,
  },
)

// `config` lets a caller opt out of the global error handling.
const deleteFileFromRecord = (payload, company = '{currentCompanySlug}', config = {}) => httpClient.delete(
  `${company}/quotes/${payload.recordId}/file-attachment/${payload.fileId}`,
  config,
)

const restoreRecordFile = (payload, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/recycle-bin/${payload.recordId}/file/${payload.fileId}`,
)

export const loadRecordCustomFieldsConfiguration = (forceCompany = null, params = {}) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/records/customFields/configurations`,
  {
    params,
  },
)

export const loadRecordSections = (forceCompany = null) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/records/customFields/sections`,
)

const createStatus = (payload) => httpClient.post('{currentCompanySlug}/quotes/statuses', payload)

const changeStatusesOrder = (payload) => httpClient.post('{currentCompanySlug}/quotes/statuses/reorder', payload)

const deleteStatus = ({ statusId, params }) => httpClient.delete(
  `{currentCompanySlug}/quotes/statuses/${statusId}`,
  {
    params,
  },
)

const updateStatus = (payload) => httpClient.put(`{currentCompanySlug}/quotes/statuses/${payload.id}`, payload)

const loadFiltersViews = () => httpClient.get('{currentCompanySlug}/filter-views/quotes')

export const loadFiltersViewsForCompany = (company) =>
  httpClient.get(`${company}/filter-views/quotes`)

export const loadAllUnfavoriteCompanyFiltersViews = (company) =>
  httpClient.get(`${company}/filter-views?favourite=0`)

export const updateFavoriteViewForCompany = (payload, company) =>
  httpClient.post(`${company}/filter-views/quotes/favourite`, { data: payload })

export const getRecordsForCompany = (company, filters) =>
  httpClient.post(`${company}/quotes/all`, filters)

export const loadFiltersViewsCounts = (ids, forceCompany = null) => httpClient.get(
  `${forceCompany || '{currentCompanySlug}'}/filter-views/quotes/counts`,
  {
    params: { ids },
  },
)

const createFiltersView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes', payload)

const reorderFiltersView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/reorder', payload)

const deleteFiltersView = (filterViewId) => httpClient.delete(`{currentCompanySlug}/filter-views/quotes/${filterViewId}`)

const updateFiltersView = (payload) => httpClient.patch(
  `{currentCompanySlug}/filter-views/quotes/${payload.filterViewId}`, payload.data,
)

const updateFiltersViewHidden = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/hide', { data: payload })

const updateFavoriteView = (payload) => httpClient.post('{currentCompanySlug}/filter-views/quotes/favourite', { data: payload })

const getRecordCollaborators = (payload, company = '{currentCompanySlug}') => httpClient.get(`${company}/records/${payload.recordId}/collaborators`, {
  ignoreGlobalNotFoundCatch: true,
})

const signTemplate = ({ orderId, templateId, payload, company = '{currentCompanySlug}' }) => httpClient.post(
  `${company}/quotes/${orderId}/template-document/${templateId}/e-sign`,
  payload,
)
const createEmbedLinkToSignTemplate = ({ orderId, templateId, payload, company = '{currentCompanySlug}' }) => {
  return httpClient.post(
    `${company}/quotes/${orderId}/template-document/${templateId}/e-sign/createTemplateEmbedLink`,
    payload,
  )
}

export const loadCustomFieldChangeLog = ({ recordId, customFieldSlug }, page, company = '{currentCompanySlug}') => {
  return httpClient.get(`${company}/records/${recordId}/change-log/${customFieldSlug}`, {
    params: {
      page,
    },
  })
}

// Everything the record Gantt plots, for one date window: task rows across every
// section the record reaches, and the date fields it can chart. One request
// replaces the per-section fan-out plus the account lookups the chart used to
// need, and it is the only reader of the window — rows outside it are counted
// rather than sent.
export const getRecordGantt = (recordId, { from, to }, company = '{currentCompanySlug}', config = {}) => {
  return httpClient.get(`/${company}/records/${recordId}/gantt`, { params: { from, to }, ...config })
}

// See getObjectChildren on `config`: default empty, so existing callers are
// unaffected, and used by the Gantt to opt out of the global 403/404 handling.
export const getSharedTableData = (recordId, linkSlug, childObjectSlug, company = '{currentCompanySlug}', config = {}) => {
  return httpClient.get(`/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}`, config)
}

// Depends On / Required For for the rows of a shared table. A paginated page
// cannot resolve either locally — the target may sit on another page, and
// Required For needs a reverse lookup — so the ids on screen are sent and the
// server answers with both directions per row. `ids` must not be empty (422),
// and rows outside the link's scope are simply absent from the response.
export const getSharedTableTaskLinks = (
  recordId,
  linkSlug,
  childObjectSlug,
  ids,
  company = '{currentCompanySlug}',
  config = {},
) => {
  return httpClient.get(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/task-links`,
    { params: { ids }, ...config },
  )
}

// Paginated rows of the pooled child object table of a multi-link section:
// GET .../shared-tables/{linkSlug}/{childObjectSlug}/rows?page&per_page&sort_column&sort_order
// The `/rows` suffix is what separates it from getSharedTableData above, which
// serves single-link sections and is unaffected by this endpoint.
export const getMultiLinkSharedTableRows = (recordId, linkSlug, childObjectSlug, params, company = '{currentCompanySlug}') => {
  return httpClient.get(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/rows`,
    { params },
  )
}

// One pooled row created or updated. The payload (built by
// buildMultiLinkSharedTableRowPayload) names the owning linked record through
// its own `recordId`, so the single-link write endpoint has everything it needs
// and no multi-link-specific path is required. The config matches
// updateSharedTableData: the global 403 handler is skipped so the caller can
// read an automation validation message out of the response itself.
// Verified against a real multi-link section: this endpoint accepts a payload
// whose `recordId` names a linked record of a section that allows multiple
// links, so no multi-link-specific write endpoint is needed.
export const saveMultiLinkSharedTableRow = (recordId, linkSlug, payload, company = '{currentCompanySlug}') => {
  return httpClient.patch(`/${company}/records/${recordId}/shared-tables/${linkSlug}`, payload, {
    skipDefaultForbiddenBehaviour: true,
    ignoreGlobalNotify: true,
  })
}

export const reorderSharedTable = (recordId, linkSlug, childObjectSlug, linkedRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.put(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/${linkedRecordId}/reorder`,
    payload,
  )
}

export const sortSharedTable = (recordId, linkSlug, childObjectSlug, linkedRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.put(
    `/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/${linkedRecordId}`,
    payload,
  )
}
export const updateSharedTableData = (recordId, linkSlug, payload, company = '{currentCompanySlug}') => {
  return httpClient.patch(`/${company}/records/${recordId}/shared-tables/${linkSlug}`, payload, {
    skipDefaultForbiddenBehaviour: true,
    ignoreGlobalNotify: true,
  })
}

export const getManyToManyLinkData = (customFiledId, params, company = '{currentCompanySlug}') => {
  return httpClient.get(`/${company}/quotes/customFields/${customFiledId}/linkedRecords`, {
    params: {
      per_page: 20,
      ...params,
    },
    ignoreGlobalNotFoundCatch: true,
  })
}
export const getManyToManyAllLinkedRecords = (customFiledId, company = '{currentCompanySlug}') => {
  return httpClient.get(`/${company}/quotes/customFields/${customFiledId}/allLinkedRecords`)
}

export const deleteManyToManyLink = (customFiledId, recordId, company = '{currentCompanySlug}') => {
  return httpClient.delete(`/${company}/quotes/customFields/${customFiledId}/linkedRecords?id=${recordId}`)
}

export const addManyToManyLink = (customFiledId, recordId, company = '{currentCompanySlug}') => {
  return httpClient.post(`/${company}/quotes/customFields/${customFiledId}/linkedRecords`, {
    id: recordId,
  })
}

export const deleteSharedTableRow = (recordId, linkSlug, childObjectSlug, data, company = '{currentCompanySlug}') => {
  return httpClient.delete(`/${company}/records/${recordId}/shared-tables/${linkSlug}/${childObjectSlug}/`, {
    data,
  })
}

export const mergeRecords = (baseRecordId, payload, company = '{currentCompanySlug}') => {
  return httpClient.patch(`/${company}/quotes/${baseRecordId}/merge`, payload)
}

export const calculateFields = (payload) => {
  return httpClient.post(`/{currentCompanySlug}/filter-views/quotes/calculate`, payload)
}

export const restoreRecord = (recordId) => {
  return httpClient.post(`/{currentCompanySlug}/recycle-bin/${recordId}`)
}

export const getVAiFieldConfiguration = (company = '{currentCompanySlug}') => {
  return httpClient.get(
    `${company}/vsimple-ai/field-configurations`,
  ).then(({ data }) => {
    return data
  })
}

export const updateVAiFieldConfiguration = (slug, field) => httpClient.put(
  `{currentCompanySlug}/vsimple-ai/field-configurations/${slug}`,
  field,
)

export const forceProcessVAiFile = (fileId, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/vsimple-ai/files/${fileId}/force-process`,
)

export const forceProcessVAiResource = (resourceId, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/vsimple-ai/resources/${resourceId}/force-process`,
)

// Record-page background checks: the callers degrade quietly, so no toast either.
export const getFilesHasSummary = (recordId, company = '{currentCompanySlug}') => vaiClient.get(
  `${import.meta.env.VITE_AI_BASE_URL}/${company}/records/${recordId}/attachments`,
  { ignoreGlobalNotify: true },
)

export const getFilesSummaryStatus = (recordId, company = '{currentCompanySlug}') => vaiClient.get(
  `${import.meta.env.VITE_AI_BASE_URL}/${company}/records/${recordId}/processing_status`,
  { ignoreGlobalNotify: true },
)

export const launchCpqIntegration = (recordId, integrationSlug, payload = {}) => httpClient.post(
  `{currentCompanySlug}/records/${recordId}/cpq/${integrationSlug}/quotes`,
  Object.keys(payload).length ? { meta: payload } : undefined,
  {
    skipDefaultForbiddenBehaviour: true,
    ignoreGlobalNotify: true,
  },
)

export const storeCpqIntegrationXmlData = (payload) => httpClient.post(
  `{currentCompanySlug}/cpq/quotes/store-data`,
  payload,
)

export const getCpqIntegrationQuotes = (recordId) => httpClient.get(
  `{currentCompanySlug}/records/${recordId}/cpq/quotes`,
)

export const closeLiftnetQuote = (recordId, quoteId) => httpClient.get(
  `{currentCompanySlug}/records/${recordId}/cpq/liftnet2/quotes/${quoteId}/close-url`,
)

export const postMessageReaction = (id, uuid, payload, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/${id}/activity-stream/messages/${uuid}/reactions`,
  payload,
)

export const deleteMessageReaction = (id, uuid, payload, company = '{currentCompanySlug}') => httpClient.delete(
  `${company}/quotes/${id}/activity-stream/messages/${uuid}/reactions`,
  {
    data: payload,
  },
)

export const getRepliedByMessages = (id, params, company = '{currentCompanySlug}') => httpClient.get(
  `${company}/quotes/${id}/activity-stream/groups`, { params })

// `company` names the workflow the records live in: a link field points at its connected
// flow, and records of that flow are not readable through the current company's endpoint.
export const getRecordsByIds = (ids, company = '{currentCompanySlug}') => httpClient.post(
  `${company}/quotes/by-ids`,
  { ids },
)

export const autocompleteRecords = (data) => httpClient({
  method: 'POST',
  url: `${import.meta.env.VITE_UNIVERSAL_SEARCH_BASE_URL}/{currentCompanySlug}/records/autocomplete`,
  data,
  withCredentials: false,
})

export default {
  getRecords,
  getKanbanRecords,
  getCardRecords,
  getRecordsCount,
  createRecord,
  getRecord,
  deleteRecord,
  getRecordActivityStream,
  getRecordFiles,
  moveRecordFilesToSubfolder,
  renameRecordFileSubfolder,
  getRecordFileGroups,
  createMessage,
  updateMessage,
  readRecordStatuses,
  updateRecordStatus,
  updateRecordType,
  addCollaborators,
  removeCollaborators,
  moveChildRecord,
  moveChildRecordMultiple,
  editRecord,
  exportRecords,
  getFiltersStructure,
  deleteObjectChild,
  editObjectChild,
  createObjectChildren,
  getObjectChildren,
  getObjectChildrenMeta,
  patchObjectField,
  sortObjectFiedlds,
  templateDocumentsList,
  getPersistentColumns,
  savePersistentColumns,
  deleteFileFromRecord,
  restoreRecordFile,
  loadRecordCustomFieldsConfiguration,
  createStatus,
  changeStatusesOrder,
  deleteStatus,
  updateStatus,
  loadFiltersViews,
  createFiltersView,
  deleteFiltersView,
  updateFiltersView,
  reorderFiltersView,
  updateFiltersViewHidden,
  updateFavoriteView,
  getRecordCollaborators,
  patchObjectFieldDryRun,
  loadRecordSections,
  signTemplate,
  loadFiltersViewsCounts,
  getSharedTableData,
  getMultiLinkSharedTableRows,
  saveMultiLinkSharedTableRow,
  deleteSharedTableRow,
  updateSharedTableData,
  mergeRecords,
  calculateFields,
  createEmbedLinkToSignTemplate,
  restoreRecord,
  getManyToManyLinkData,
  getRecordActivityStreamGroup,
  getVAiFieldConfiguration,
  updateVAiFieldConfiguration,
  forceProcessVAiFile,
  forceProcessVAiResource,
  getFilesHasSummary,
  getFilesSummaryStatus,
  launchCpqIntegration,
  storeCpqIntegrationXmlData,
  getCpqIntegrationQuotes,
  closeLiftnetQuote,
  postMessageReaction,
  deleteMessageReaction,
  getRepliedByMessages,
  loadFiltersViewsForCompany,
  updateFavoriteViewForCompany,
  getRecordsForCompany,
}
